import React, { useState, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, PolylineF, InfoWindowF } from '@react-google-maps/api';

const containerStyle = {
	width: '100%',
	height: '600px'
};

const defaultCenter = {
	lat: 19.4326,
	lng: -99.1332
};

const polylineOptions = {
	strokeColor: '#007bff',
	strokeOpacity: 0.8,
	strokeWeight: 3
};

const TrackingRouteMap = ({ locations, scheduleLocations }) => {
	const { isLoaded, loadError } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
	});

	const [map, setMap] = useState(null);
	const [selectedPoint, setSelectedPoint] = useState(null);
	const [selectedStore, setSelectedStore] = useState(null);
	const [initialCenter, setInitialCenter] = useState(null);

	// Sort locations by date (oldest first) and create path
	const sortedLocations = useMemo(() => {
		if (!locations || locations.length === 0) return [];
		return [...locations].sort((a, b) => {
			const dateA = a.date?.$date || a.updated || a.created || 0;
			const dateB = b.date?.$date || b.updated || b.created || 0;
			return dateA - dateB;
		});
	}, [locations]);

	// Create path for polyline
	const path = useMemo(() => {
		return sortedLocations.map(loc => ({
			lat: loc.lat,
			lng: loc.lon
		}));
	}, [sortedLocations]);

	const onLoad = useCallback((map) => {
		setMap(map);

		// Fit bounds to show all markers (only on initial load)
		if (sortedLocations.length > 0) {
			const bounds = new window.google.maps.LatLngBounds();
			sortedLocations.forEach(loc => {
				bounds.extend({ lat: loc.lat, lng: loc.lon });
			});
			map.fitBounds(bounds);

			// Set initial center
			setInitialCenter({
				lat: sortedLocations[0].lat,
				lng: sortedLocations[0].lon
			});
		}
	}, [sortedLocations]);

	const onUnmount = useCallback(() => {
		setMap(null);
		setInitialCenter(null);
	}, []);

	// Create numbered icon
	const createNumberedIcon = (number) => {
		if (!window.google || !window.google.maps) {
			return undefined;
		}

		return {
			url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
				<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
					<path fill="#EA4335" d="M20,0 C8.954,0 0,8.954 0,20 C0,32 20,50 20,50 C20,50 40,32 40,20 C40,8.954 31.046,0 20,0 Z"/>
					<circle cx="20" cy="20" r="12" fill="white"/>
					<text x="20" y="26" font-size="${number > 99 ? '10' : '14'}" font-weight="bold" text-anchor="middle" fill="#EA4335">${number}</text>
				</svg>
			`)}`,
			scaledSize: new window.google.maps.Size(40, 50),
			anchor: new window.google.maps.Point(20, 50)
		};
	};

	const createStoreIcon = () => {
		if (!window.google || !window.google.maps) {
			return undefined;
		}

		return {
			url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
				<svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
					<path fill="#34A853" d="M18,0 C8.059,0 0,8.059 0,18 C0,29 18,46 18,46 C18,46 36,29 36,18 C36,8.059 27.941,0 18,0 Z"/>
					<circle cx="18" cy="18" r="11" fill="white"/>
					<svg x="8" y="8" width="20" height="20" viewBox="0 -960 960 960">
						<path fill="#34A853" d="M160-720v-80h640v80H160Zm0 560v-240h-40v-80l40-200h640l40 200v80h-40v240h-80v-240H560v240H160Zm80-80h240v-160H240v160Zm-38-240h556-556Zm0 0h556l-24-120H226l-24 120Z"/>
					</svg>
				</svg>
			`)}`,
			scaledSize: new window.google.maps.Size(36, 46),
			anchor: new window.google.maps.Point(18, 46)
		};
	};

	const formatDate = (timestamp) => {
		if (!timestamp) return 'N/A';
		// If timestamp is in milliseconds (> year 2000 in seconds), use directly
		// Otherwise multiply by 1000
		const ms = timestamp > 1000000000000 ? timestamp : timestamp * 1000;
		const date = new Date(ms);
		return date.toLocaleString('es-MX', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'America/Mexico_City'
		});
	};

	const getCenter = () => {
		if (initialCenter) {
			return initialCenter;
		}
		if (sortedLocations.length > 0) {
			return {
				lat: sortedLocations[0].lat,
				lng: sortedLocations[0].lon
			};
		}
		return defaultCenter;
	};

	if (loadError) {
		return (
			<div className="alert alert-danger text-center mt-4">
				Error al cargar Google Maps: {loadError.message}
			</div>
		);
	}

	if (!isLoaded) {
		return (
			<div className="d-flex justify-content-center align-items-center" style={{ height: '600px' }}>
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading map...</span>
				</div>
			</div>
		);
	}

	return (
		<GoogleMap
			mapContainerStyle={containerStyle}
			center={!map ? getCenter() : undefined}
			zoom={12}
			onLoad={onLoad}
			onUnmount={onUnmount}
			options={{ gestureHandling: 'greedy', streetViewControl: false, mapTypeControl: false, fullscreenControl: false, zoomControl: false }}
		>
			{/* Polyline connecting all points */}
			{path.length > 1 && (
				<PolylineF
					path={path}
					options={polylineOptions}
				/>
			)}

			{/* Numbered markers */}
			{sortedLocations.map((location, index) => (
				<MarkerF
					key={index}
					position={{ lat: location.lat, lng: location.lon }}
					icon={createNumberedIcon(index + 1)}
					title={`Punto ${index + 1}`}
					onClick={() => { setSelectedPoint({ ...location, index: index + 1 }); setSelectedStore(null); }}
				/>
			))}

			{/* Info window for selected point */}
			{selectedPoint && (
				<InfoWindowF
					position={{ lat: selectedPoint.lat, lng: selectedPoint.lon }}
					onCloseClick={() => setSelectedPoint(null)}
					options={{ disableAutoPan: true }}
				>
					<div style={{ minWidth: '180px', color: '#000' }}>
						<p className="mb-1"><strong>Ubicación {selectedPoint.index}</strong></p>
						<p className="mb-1"><strong>Coords:</strong> {selectedPoint.lat.toFixed(6)}, {selectedPoint.lon.toFixed(6)}</p>
						<p className="mb-1"><strong>Fecha:</strong> {formatDate(selectedPoint.date?.$date || selectedPoint.updated || selectedPoint.created)}</p>
					</div>
				</InfoWindowF>
			)}
			{/* Schedule store location markers */}
			{scheduleLocations && scheduleLocations.length > 0 && scheduleLocations.map((store, index) => (
				store.lat && store.lon ? (
					<MarkerF
						key={`store-${store._id?.$oid || index}`}
						position={{ lat: store.lat, lng: store.lon }}
						icon={createStoreIcon()}
						title={store.name || `Tienda ${index + 1}`}
						onClick={() => { setSelectedStore(store); setSelectedPoint(null); }}
					/>
				) : null
			))}

			{/* Info window for selected store */}
			{selectedStore && (
				<InfoWindowF
					position={{ lat: selectedStore.lat, lng: selectedStore.lon }}
					onCloseClick={() => setSelectedStore(null)}
					options={{ disableAutoPan: true }}
				>
					<div style={{ minWidth: '180px', color: '#000' }}>
						<p className="mb-1"><strong>{selectedStore.name || 'N/A'}</strong></p>
						<p className="mb-1"><strong>Código:</strong> {selectedStore.code || 'N/A'}</p>
						{selectedStore.frecuency != null && (
							<p className="mb-1"><strong>Frecuencia:</strong> {selectedStore.frecuency}</p>
						)}
					</div>
				</InfoWindowF>
			)}
		</GoogleMap>
	);
};

export default TrackingRouteMap;
